<template>
  <div class="register-form">
    <div class="register-layout">
      <aside class="setup-summary">
        <div class="setup-summary__title">Setup Progress</div>
        <div class="setup-summary__sub">{{ completedFields }} of {{ totalFields }} key details complete</div>
        <div class="progress-track">
          <div class="progress-track__bar" :style="{ width: completionPercent + '%' }"></div>
        </div>
        <div class="summary-list">
          <div
            class="summary-item"
            :class="{ 'summary-item--done': company.company_name && company.company_sub_type }"
          >
            <v-icon small>{{ company.company_name && company.company_sub_type ? "check_circle" : "radio_button_unchecked" }}</v-icon>
            <span>School identity</span>
          </div>
          <div
            class="summary-item"
            :class="{ 'summary-item--done': company.email && company.password }"
          >
            <v-icon small>{{ company.email && company.password ? "check_circle" : "radio_button_unchecked" }}</v-icon>
            <span>Access details</span>
          </div>
          <div
            class="summary-item"
            :class="{ 'summary-item--done': company.port && company.company_no && company.domain }"
          >
            <v-icon small>{{ company.port && company.company_no && company.domain ? "check_circle" : "radio_button_unchecked" }}</v-icon>
            <span>Server route</span>
          </div>
        </div>
        <div class="summary-callout">
          <v-icon small>info</v-icon>
          <span>Server values are generated from the school name and port.</span>
        </div>
      </aside>

      <div class="register-main">
        <!-- Section: School Identity -->
        <div class="form-section">
          <div class="form-section__header">
            <div class="form-section__icon-wrap">
              <v-icon class="form-section__icon">school</v-icon>
            </div>
            <div>
              <div class="form-section__title">School Identity</div>
              <div class="form-section__sub">Basic information about this school</div>
            </div>
          </div>
          <div class="form-section__body">
            <v-layout row wrap>
              <v-flex xs12 sm8 class="pr-sm-3 mb-3">
                <form-text-field
                  v-model="company.company_name"
                  label="School Name"
                  required
                  :rules="[rules.required]"
                  placeholder="e.g. St. Mary's Primary School"
                ></form-text-field>
              </v-flex>
              <v-flex xs12 sm4 class="mb-3">
                <form-select-field
                  :items="[{ text: 'Primary', value: 1 }, { text: 'Secondary', value: 2 }]"
                  v-model="company.company_sub_type"
                  label="School Type"
                  required
                  placeholder="Select type"
                ></form-select-field>
              </v-flex>
            </v-layout>

            <v-layout row wrap v-if="company.company_id">
              <v-flex xs12 class="mb-3">
                <form-text-field
                  v-model="company.company_id"
                  label="School ID"
                  :disabled="true"
                  prepend-inner-icon="fingerprint"
                  readonly-style
                ></form-text-field>
              </v-flex>
            </v-layout>
          </div>
        </div>

        <v-divider class="section-divider"></v-divider>

        <!-- Section: Contact & Access -->
        <div class="form-section">
      <div class="form-section__header">
        <div class="form-section__icon-wrap form-section__icon-wrap--teal">
          <v-icon class="form-section__icon">contact_mail</v-icon>
        </div>
        <div>
          <div class="form-section__title">Contact &amp; Access</div>
          <div class="form-section__sub">Login credentials and contact details</div>
        </div>
      </div>
      <div class="form-section__body">
        <v-layout row wrap>
          <v-flex xs12 sm5 class="pr-sm-3 mb-3">
            <form-text-field
              v-model="company.email"
              label="Email"
              required
              :rules="[rules.required, rules.email]"
              placeholder="admin@school.edu.au"
              prepend-inner-icon="email"
            ></form-text-field>
          </v-flex>
          <v-flex xs12 sm4 class="pr-sm-3 mb-3">
            <form-text-field
              v-model="company.mobile"
              label="Phone"
              placeholder="+61 4xx xxx xxx"
              prepend-inner-icon="phone"
            ></form-text-field>
          </v-flex>
          <v-flex xs12 sm3 class="mb-3">
            <form-text-field
              v-model="company.password"
              label="Password"
              required
              :rules="[rules.required]"
              type="password"
              placeholder="••••••••"
              prepend-inner-icon="lock"
            >
              <v-btn
                slot="label-action"
                v-if="company && company.password && company.password.length > 15"
                x-small
                flat
                color="warning"
                class="field-action-btn"
                @click="openConfirm('reset')"
              >Reset</v-btn>
            </form-text-field>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs12 sm4 class="mb-3">
            <form-text-field
              v-model="company.resource_limit"
              label="Resource Limit"
              required
              :rules="[rules.required]"
              type="number"
              placeholder="100"
              prepend-inner-icon="people"
            ></form-text-field>
          </v-flex>
        </v-layout>
      </div>
        </div>

        <v-divider class="section-divider"></v-divider>

        <!-- Section: Server Configuration -->
        <div class="form-section">
      <div class="form-section__header">
        <div class="form-section__icon-wrap form-section__icon-wrap--dark">
          <v-icon class="form-section__icon">dns</v-icon>
        </div>
        <div>
          <div class="form-section__title">Server Configuration</div>
          <div class="form-section__sub">Generated fields, editable for managed schools</div>
        </div>
      </div>
      <div class="form-section__body">
        <v-layout row wrap>
          <v-flex xs12 sm6 class="pr-sm-3 mb-3">
            <form-text-field
              v-model="company.company_no"
              label="Server Identifier"
              disabled
              prepend-inner-icon="vpn_key"
              readonly-style
            ></form-text-field>
          </v-flex>
          <v-flex xs12 sm6 class="mb-3">
            <form-text-field
              v-model="company.timezone"
              label="Timezone"
              :disabled="true"
              prepend-inner-icon="schedule"
              readonly-style
            ></form-text-field>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs12 sm5 class="pr-sm-3 mb-3">
            <form-text-field
              v-model="company.url"
              label="Server URL"
              :rules="[rules.required]"
              prepend-inner-icon="link"
              placeholder="http://localhost"
            ></form-text-field>
          </v-flex>
          <v-flex xs12 sm2 class="pr-sm-3 mb-3">
            <form-text-field
              v-model="company.port"
              label="Port"
              required
              :rules="[rules.required]"
              type="number"
              placeholder="3000"
              prepend-inner-icon="settings_ethernet"
            ></form-text-field>
          </v-flex>
          <v-flex xs12 sm5 class="mb-3">
            <form-text-field
              v-model="company.domain"
              label="Domain"
              :rules="[rules.required]"
              :disabled="!company._id"
              prepend-inner-icon="public"
              :readonly-style="!company._id"
            ></form-text-field>
          </v-flex>
        </v-layout>
      </div>
        </div>

        <slot name="imageholder"></slot>

        <v-divider class="section-divider"></v-divider>

        <!-- Action bar -->
        <div class="form-actions">
      <div class="form-actions__left">
        <v-btn
          v-if="company._id"
          flat
          color="error"
          class="action-btn action-btn--danger"
          @click="handleDelete"
        >
          <v-icon left small>delete</v-icon>Delete School
        </v-btn>
      </div>
      <div class="form-actions__right">
        <v-btn
          v-if="draft"
          flat
          color="secondary"
          class="action-btn"
          @click="openConfirm('discard')"
        >
          <v-icon left small>undo</v-icon>Discard Draft
        </v-btn>
        <v-btn
          depressed
          color="primary"
          class="action-btn action-btn--primary"
          :loading="submitting"
          @click="createCompany"
        >
          <v-icon left small>{{ company._id ? 'save' : 'add' }}</v-icon>
          {{ company._id ? 'Save Changes' : 'Register School' }}
        </v-btn>
      </div>
        </div>
      </div>
    </div>

    <v-dialog v-model="confirmDialog.open" max-width="460px">
      <v-card class="confirm-dialog">
        <div class="modal-header">
          <div class="modal-header__left">
            <div
              class="modal-header__icon-wrap"
              :class="{
                'modal-header__icon-wrap--danger': confirmDialog.type === 'delete',
                'modal-header__icon-wrap--warning': confirmDialog.type !== 'delete'
              }"
            >
              <v-icon class="modal-header__icon">{{ confirmDialog.icon }}</v-icon>
            </div>
            <div>
              <div class="modal-header__title">{{ confirmDialog.title }}</div>
              <div class="modal-header__sub">{{ confirmDialog.subtitle }}</div>
            </div>
          </div>
          <v-btn icon flat class="modal-close-btn" :disabled="confirmDialog.loading" @click="closeConfirm">
            <v-icon>close</v-icon>
          </v-btn>
        </div>

        <v-divider class="modal-divider"></v-divider>

        <div class="modal-body">
          <p class="confirm-copy">{{ confirmDialog.message }}</p>
        </div>

        <v-divider class="modal-divider"></v-divider>

        <div class="modal-footer">
          <v-btn flat class="modal-cancel-btn" :disabled="confirmDialog.loading" @click="closeConfirm">
            Cancel
          </v-btn>
          <v-btn
            depressed
            :color="confirmDialog.type === 'delete' ? 'error' : 'primary'"
            class="modal-action-btn"
            :loading="confirmDialog.loading"
            @click="confirmAction"
          >
            <v-icon left small>{{ confirmDialog.actionIcon }}</v-icon>
            {{ confirmDialog.actionText }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import APICompany from "@/mixins/APICompany";
import ConfirmDelete from "@/mixins/confirmDelete";
import FormSelectField from "@/components/form/FormSelectField";
import FormTextField from "@/components/form/FormTextField";
export default {
  components: {
    FormSelectField,
    FormTextField
  },
  mixins: [APICompany, ConfirmDelete],
  props: {
    company: {
      type: Object,
      default: function() {
        return {
          company_no: "edpass_",
          resource_limit: 100,
          password: ""
        };
      }
    }
  },
  data() {
    return {
      draft: false,
      email: "",
      port: "",
      schools: [],
      submitting: false,
      confirmDialog: {
        open: false,
        loading: false,
        type: "",
        icon: "help_outline",
        actionIcon: "check",
        title: "",
        subtitle: "",
        message: "",
        actionText: "Confirm"
      },
      rules: {
        required: value => !!value || "Required.",
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail.";
        }
      }
    };
  },
  watch: {
    company: {
      handler: function() {
        this.savePendingSchool();
        if (this.port && !this.company.port) this.company.port = this.port;
        if (!this.company.timezone) this.company.timezone = "Australia/Sydney";
        if (!this.company.url) this.company.url = "http://localhost";
        if (!this.company.domain && this.company.port) {
          this.company.domain = "https://portal.edpass.co/" + this.company.port;
        }
        this.syncServerIdentifierPort();
      },
      deep: true
    }
  },
  computed: {
    completedFields() {
      return [
        this.company.company_name,
        this.company.company_sub_type,
        this.company.email,
        this.company.password,
        this.company.resource_limit,
        this.company.port
      ].filter(Boolean).length;
    },
    totalFields() {
      return 6;
    },
    completionPercent() {
      return Math.round((this.completedFields / this.totalFields) * 100);
    }
  },
  mounted() {
    this.resumePendingSchool();
    this.listRegisteredSchools();
  },
  methods: {
    normalizeDomain: function(value) {
      const domain = String(value || "").trim();
      if (!domain) return domain;
      if (/^https?:\/\//i.test(domain)) return domain;
      return `https://${domain}`;
    },
    buildCompanyPayload: function() {
      const fields = [
        "_id",
        "company_id",
        "company_no",
        "company_name",
        "company_type",
        "company_sub_type",
        "email",
        "mobile",
        "resource_limit",
        "status",
        "lat",
        "long",
        "url",
        "port",
        "timezone",
        "domain",
        "modules"
      ];
      let payload = {};

      fields.forEach(key => {
        if (this.company[key] !== undefined) payload[key] = this.company[key];
      });

      payload.domain = this.normalizeDomain(payload.domain);
      payload.port = payload.port !== undefined ? String(payload.port).trim() : payload.port;
      payload.url = payload.url !== undefined ? String(payload.url).trim() : payload.url;
      return payload;
    },
    createIdentifierBase: function() {
      const schoolName = String(this.company.company_name || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      if (!this.company._id && schoolName) {
        return ["edpass", schoolName].join("_");
      }

      const current = String(this.company.company_no || "").trim();
      const currentBase = current.replace(/_\d+$/, "").replace(/_+$/, "");
      if (currentBase && currentBase !== "edpass") return currentBase;

      return schoolName ? ["edpass", schoolName].join("_") : "edpass";
    },
    syncServerIdentifierPort: function() {
      if (!this.company) return;
      const port = String(this.company.port || "").trim();
      if (!port) return;

      const nextIdentifier = [this.createIdentifierBase(), port].join("_");
      if (this.company.company_no !== nextIdentifier) {
        this.company.company_no = nextIdentifier;
      }
    },
    listRegisteredSchools: async function() {
      try {
        this.schools = await this.getCompanies();
        let maxPort = this.schools.map(obj => parseInt(obj.port)).sort().pop();
        if (maxPort) this.port = String(maxPort + 1);
      } catch (error) {
        // Keep the current form state if the schools list is unavailable.
      }
    },
    savePendingSchool: function() {
      if (this.company._id) return;
      sessionStorage.newCompany = JSON.stringify(this.company);
      this.draft = true;
    },
    removePendingSchool: function() {
      sessionStorage.removeItem("newCompany");
      this.$router.push({ name: "Company" });
    },
    resumePendingSchool: function() {
      if (sessionStorage.newCompany) {
        Object.assign(this.company, JSON.parse(sessionStorage.newCompany) || {});
        this.draft = true;
      } else {
        this.draft = false;
      }
    },
    openConfirm: function(type) {
      const dialogs = {
        reset: {
          type: "reset",
          icon: "lock",
          actionIcon: "vpn_key",
          title: "Reset password?",
          subtitle: "Temporary password will be applied",
          message: 'This will replace the current school admin password with "123456".',
          actionText: "Reset Password"
        },
        discard: {
          type: "discard",
          icon: "undo",
          actionIcon: "delete_sweep",
          title: "Discard draft?",
          subtitle: "Unsaved school details will be removed",
          message: "The current draft will be cleared from this browser and you will return to the schools list.",
          actionText: "Discard Draft"
        }
      };
      this.confirmDialog = Object.assign({ open: true, loading: false }, dialogs[type]);
    },
    closeConfirm: function() {
      if (this.confirmDialog.loading) return;
      this.confirmDialog.open = false;
    },
    confirmAction: async function() {
      this.confirmDialog.loading = true;
      try {
        if (this.confirmDialog.type === "reset") {
          await this.resetPassword();
        } else if (this.confirmDialog.type === "discard") {
          this.removePendingSchool();
        }
      } catch (error) {
        this.$toast.error("Action failed", "Error", { position: "topCenter" });
      } finally {
        this.confirmDialog.loading = false;
        this.confirmDialog.open = false;
      }
    },
    resetPassword: async function() {
      if (!this.company._id) return;
      await this.putCompany({ _id: this.company._id, password: "123456" });
      this.$toast.success(
        'Password reset to "123456" successfully.',
        "Success",
        { position: "topCenter" }
      );
    },
    handleDelete: async function() {
      const result = await this.confirmDeleteAction({
        title: "Delete this school?",
        text: "The school record and its related setup details will be removed permanently.",
        confirmButtonText: "Delete School"
      });
      if (!result || !result.value) return;
      await this.removeCompany();
      this.$router.push({ name: "Company" });
    },
    removeCompany: async function() {
      try {
        if (this.company._id) {
          await this.deleteCompany(this.company._id);
          this.$toast.success("School deleted successfully.", "Success", { position: "topCenter" });
        }
      } catch (error) {
        this.$router.push({ name: "Company" });
      }
    },
    createCompany: async function() {
      this.submitting = true;
      try {
        this.syncServerIdentifierPort();
        if (this.company._id) {
          await this.putCompany(this.buildCompanyPayload());
        } else {
          let ob = this.buildCompanyPayload();
          ob.password = this.company.password;
          ob.company_id = ob.company_no;
          let response = await this.postCompany(ob);
          this.$store.commit("setStudentSwitchState", { action: null, payload: response });
        }
        sessionStorage.removeItem("newCompany");
        this.draft = false;
        this.$toast.success("Changes saved successfully.", "Success", { position: "topCenter" });
      } catch (error) {
        const message = error && error.response && error.response.data && (error.response.data.message || error.response.data.error)
          ? error.response.data.message || error.response.data.error
          : "Failed to submit details";
        this.$toast.error(message, "Error", { position: "topCenter" });
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped lang="css">
.register-form {
  padding: 0;
  background-color: #ffffff;
}

.register-layout {
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  gap: 20px;
}

.setup-summary {
  align-self: start;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  position: sticky;
  top: 84px;
}

.setup-summary__title {
  color: #0f172a;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
}

.setup-summary__sub {
  color: #64748b;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  line-height: 1.45;
  margin-top: 4px;
}

.progress-track {
  background: #e2e8f0;
  border-radius: 999px;
  height: 8px;
  margin: 16px 0;
  overflow: hidden;
}

.progress-track__bar {
  background: #1565C0;
  border-radius: 999px;
  height: 100%;
  min-width: 8px;
  transition: width 0.2s ease;
}

.summary-list {
  display: grid;
  gap: 10px;
}

.summary-item {
  align-items: center;
  color: #64748b;
  display: flex;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 13px;
  gap: 8px;
  line-height: 1.4;
  min-height: 22px;
}

.summary-item >>> .v-icon {
  color: #94a3b8 !important;
}

.summary-item--done {
  color: #166534;
  font-weight: 700;
}

.summary-item--done >>> .v-icon {
  color: #16a34a !important;
}

.summary-callout {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  display: flex;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  gap: 8px;
  line-height: 1.5;
  margin-top: 18px;
  padding: 12px;
}

.summary-callout >>> .v-icon {
  color: #1565C0 !important;
  margin-top: 1px;
}

.register-main {
  min-width: 0;
}

/* Form sections */
.form-section {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px 16px 4px;
}

.form-section__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.form-section__icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1565C0, #0d47a1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.form-section__icon-wrap--teal {
  background: linear-gradient(135deg, #00838F, #006064);
}

.form-section__icon-wrap--dark {
  background: linear-gradient(135deg, #37474F, #263238);
}

.form-section__icon {
  color: #ffffff !important;
  font-size: 18px !important;
}

.form-section__title {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  color: #1e293b;
  letter-spacing: -0.1px;
}

.form-section__sub {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  line-height: 1.45;
  color: #94a3b8;
  margin-top: 2px;
}

.form-section__body {
  padding-left: 50px;
}

.field-action-btn {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  font-size: 11px !important;
  text-transform: none !important;
  height: 22px !important;
  padding: 0 8px !important;
  margin-left: 4px !important;
  border-radius: 5px !important;
  transition: opacity 0.2s ease;
}

.field-action-btn:hover {
  opacity: 0.8;
}

/* Section divider */
.section-divider {
  display: none;
}

/* Action bar */
.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  bottom: 0;
  padding: 16px 0 0;
  position: sticky;
  gap: 12px;
  flex-wrap: wrap;
  z-index: 2;
}

.form-actions__left {
  display: flex;
  gap: 8px;
}

.form-actions__right {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.action-btn {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  font-weight: 500 !important;
  text-transform: none !important;
  border-radius: 10px !important;
  height: 40px !important;
  padding: 0 18px !important;
  letter-spacing: 0.2px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn--primary {
  font-weight: 600 !important;
  box-shadow: 0 2px 8px rgba(21, 101, 192, 0.3) !important;
}

.action-btn--primary:hover {
  box-shadow: 0 4px 12px rgba(21, 101, 192, 0.4) !important;
}

.action-btn--danger {
  color: #F44336 !important;
  background-color: rgba(244, 67, 54, 0.05) !important;
}

/* Confirmation dialogs */
.confirm-dialog {
  border-radius: 14px !important;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
}

.modal-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.modal-header__icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1565C0, #0d47a1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.modal-header__icon-wrap--warning {
  background: linear-gradient(135deg, #F9A825, #F57F17);
}

.modal-header__icon-wrap--danger {
  background: linear-gradient(135deg, #E53935, #B71C1C);
}

.modal-header__icon {
  color: #ffffff !important;
  font-size: 20px !important;
}

.modal-header__title {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.modal-header__sub {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

.modal-close-btn {
  color: #94a3b8 !important;
}

.modal-divider {
  border-color: #f1f5f9 !important;
}

.modal-body {
  padding: 20px;
}

.confirm-copy {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
  margin: 0;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
}

.modal-cancel-btn,
.modal-action-btn {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  text-transform: none !important;
  border-radius: 10px !important;
}

.modal-cancel-btn {
  color: #64748b !important;
}

.modal-action-btn {
  font-weight: 600 !important;
  height: 40px !important;
  padding: 0 18px !important;
}

@media (max-width: 600px) {
  .register-layout {
    grid-template-columns: 1fr;
  }

  .setup-summary {
    position: static;
  }

  .register-form {
    padding: 0;
  }

  .form-section__body {
    padding-left: 0;
  }

  .form-section__header {
    padding-left: 0;
  }

  .form-section {
    padding: 14px 14px 6px;
  }

  .form-actions,
  .form-actions__left,
  .form-actions__right {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }
}
</style>
